import React from "react";
import "../styles/PrivacyPolicy.css";

const PrivacyPolicy = () => (
        <div className="PrivacyPolicy">
                <h1>Privacy Policy</h1>
                <p className="subtitle">iracingdata.com</p>
                <p>Effective Date: October 4, 2026</p>

                <h2>1. Introduction</h2>
                <p>
                        iracingdata.com ("we," "us," or "our") provides data analytics, statistics, and performance insights for the iRacing simulation racing community. This Privacy Policy explains how
                        we collect, use, disclose, and protect information when you visit our website at iracingdata.com (the "Site"). Please read this policy carefully. By using the Site, you agree to
                        the practices described in this policy.
                </p>

                <h2>2. Information We Collect</h2>

                <h3>2.1 iRacing Driver Data</h3>
                <p>
                        We obtain iRacing driver data, including iRating, Safety Rating, race results, lap times, and related performance statistics, through the iRacing API. We do not collect this
                        data directly from you; it originates from iRacing's platform.
                </p>
                <p>
                        We only display a driver's iRacing display name or customer ID if that driver has given us explicit consent. Drivers give consent by signing in with iRacing and turning on
                        &ldquo;Show my name on iRacingData&rdquo; on their Account page, and can withdraw it at any time from the same page. Drivers who have not consented appear as &ldquo;Hidden
                        Driver&rdquo;, their customer ID is not shown or linked, and they cannot be searched for or looked up on the Site.
                </p>

                <h3>2.2 Account Information</h3>
                <p>
                        If you create an account on the Site or subscribe to our Pro tier, we collect information you provide directly, such as your email address, username, and payment information.
                        Payment processing is handled by Stripe, Inc., and we do not store your full credit card details on our servers.
                </p>

                <h3>2.3 Cookies and Tracking Technologies</h3>
                <p>We use cookies and similar tracking technologies for the following purposes:</p>
                <ul>
                        <li>
                                <strong>Advertising:</strong> We use Google AdSense / Google Ad Manager to serve advertisements. These services may use cookies to display personalized ads based on your
                                browsing activity.
                        </li>
                        <li>
                                <strong>Analytics:</strong> We may use cookies to understand how visitors interact with the Site, including page views, navigation patterns, and feature usage.
                        </li>
                        <li>
                                <strong>Functionality:</strong> We use cookies to maintain your session, remember your preferences, and support the Pro subscription experience.
                        </li>
                </ul>

                <h3>2.4 Automatically Collected Information</h3>
                <p>
                        When you visit the Site, our hosting provider (Vercel) and advertising partners may automatically collect certain technical information, such as your IP address, browser type,
                        device type, operating system, referring URL, and pages visited.
                </p>

                <h2>3. How We Use Your Information</h2>
                <p>We use the information we collect to:</p>
                <ul>
                        <li>Operate, maintain, and improve the Site and its features</li>
                        <li>Display iRacing driver statistics and analytics</li>
                        <li>Process Pro subscription payments and manage your account</li>
                        <li>Serve advertisements through Google AdSense / Google Ad Manager</li>
                        <li>Communicate with you regarding your account or subscriptions</li>
                        <li>Analyze usage trends to improve user experience</li>
                        <li>Comply with legal obligations</li>
                </ul>

                <h2>4. Lawful Basis for Processing (GDPR)</h2>
                <p>If you are located in the European Economic Area (EEA) or the United Kingdom (UK), we process your personal data under the following lawful bases:</p>
                <ul>
                        <li>
                                <strong>Consent (Article 6(1)(a) GDPR):</strong> We display a driver's iRacing display name and customer ID, and make their career statistics viewable by others, only
                                with that driver's explicit consent, which can be withdrawn at any time from the Account page. Withdrawing consent does not affect processing carried out before withdrawal.
                        </li>
                        <li>
                                <strong>Legitimate Interest (Article 6(1)(f) GDPR):</strong> We process iRacing performance data to produce aggregate statistics (such as iRating distributions, series
                                popularity, lap-time and safety analysis) and to show race results in which non-consenting drivers are not identified. We have determined that this processing does not
                                override data subjects' fundamental rights, given that it is limited to competitive performance information and does not identify drivers who have not consented.
                        </li>
                        <li>
                                <strong>Contract (Article 6(1)(b) GDPR):</strong> To process your Pro subscription and provide services you have requested.
                        </li>
                        <li>
                                <strong>Consent (Article 6(1)(a) GDPR):</strong> Where applicable, for the placement of non-essential advertising cookies. You may withdraw consent at any time through
                                your browser settings or cookie preferences.
                        </li>
                </ul>

                <h2>5. Third-Party Services</h2>
                <p>We share information with the following categories of third-party service providers:</p>
                <ul>
                        <li>
                                <strong>Google (AdSense / Ad Manager):</strong> Serves advertisements on the Site. Google may collect and use data in accordance with its own privacy policy (
                                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                                        https://policies.google.com/privacy
                                </a>
                                ).
                        </li>
                        <li>
                                <strong>Stripe, Inc.:</strong> Processes payments for Pro subscriptions. Stripe's privacy policy is available at{" "}
                                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
                                        https://stripe.com/privacy
                                </a>
                                .
                        </li>
                        <li>
                                <strong>Vercel, Inc.:</strong> Hosts the Site. Vercel may process server logs containing IP addresses and request data. Vercel's privacy policy is available at{" "}
                                <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
                                        https://vercel.com/legal/privacy-policy
                                </a>
                                .
                        </li>
                </ul>
                <p>We do not sell your personal information to third parties.</p>

                <h2>6. Your Rights</h2>

                <h3>6.1 Rights for EEA/UK Residents (GDPR)</h3>
                <p>If you are located in the EEA or UK, you have the following rights under the GDPR:</p>
                <ul>
                        <li>
                                <strong>Right of Access:</strong> You may request a copy of the personal data we hold about you.
                        </li>
                        <li>
                                <strong>Right to Rectification:</strong> You may request correction of inaccurate personal data.
                        </li>
                        <li>
                                <strong>Right to Erasure:</strong> You may request deletion of your personal data. If you are an iRacing driver and wish to have your name or statistics removed from the
                                Site, you may submit a removal request and we will honor it within 30 days.
                        </li>
                        <li>
                                <strong>Right to Restrict Processing:</strong> You may request that we restrict the processing of your data under certain circumstances.
                        </li>
                        <li>
                                <strong>Right to Data Portability:</strong> You may request a machine-readable copy of your personal data.
                        </li>
                        <li>
                                <strong>Right to Object:</strong> You may object to the processing of your personal data based on legitimate interest. We will cease processing unless we have compelling
                                legitimate grounds.
                        </li>
                        <li>
                                <strong>Rights Related to Automated Decision-Making:</strong> We do not engage in automated decision-making or profiling that produces legal effects.
                        </li>
                </ul>
                <p>To exercise any of these rights, please contact us at the email address provided in Section 12 below. We will respond within 30 days of receiving your request.</p>

                <h3>6.2 Rights for California Residents (CCPA)</h3>
                <p>
                        If you are a California resident, you have the right to know what personal information we collect and how it is used, request deletion of your personal information, and opt out of
                        the sale of personal information. We do not sell personal information.
                </p>

                <h3>6.3 Driver Data Removal Requests</h3>
                <p>
                        Regardless of your location, if you are an iRacing member you can stop your name and customer ID from being shown at any time by turning off &ldquo;Show my name on
                        iRacingData&rdquo; on your Account page; this takes effect within about a minute. If you also wish to have your statistics removed from the Site, you may submit a request to
                        us and we will process it within 30 days.
                </p>

                <h2>7. Data Retention</h2>
                <p>
                        We retain iRacing driver data for as long as it remains available through the iRacing API and is relevant to the Site's analytics features. Account information is retained for as
                        long as your account is active. If you delete your account or request erasure, we will delete your personal data within 30 days, except where retention is required by law or for
                        legitimate business purposes (such as preventing fraud or resolving disputes).
                </p>

                <h2>8. Data Security</h2>
                <p>
                        We take reasonable measures to protect the information we collect, including the use of HTTPS encryption for data in transit and secure storage practices. However, no method of
                        transmission or storage is completely secure, and we cannot guarantee absolute security.
                </p>

                <h2>9. Children's Privacy</h2>
                <p>
                        The Site is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13. iRacing itself requires users to be at least 13
                        years old. If we become aware that we have collected personal data from a child under 13, we will take steps to delete it promptly.
                </p>

                <h2>10. International Data Transfers</h2>
                <p>
                        The Site is operated from the United States. If you access the Site from outside the United States, your information may be transferred to, stored, and processed in the United
                        States or other jurisdictions where our service providers operate. By using the Site, you acknowledge this transfer. For EEA/UK users, such transfers are conducted in reliance on
                        appropriate safeguards, including standard contractual clauses where applicable.
                </p>

                <h2>11. Changes to This Policy</h2>
                <p>
                        We may update this Privacy Policy from time to time. When we do, we will revise the "Effective Date" at the top of this page. We encourage you to review this policy periodically.
                        Continued use of the Site after any changes constitutes acceptance of the updated policy.
                </p>

                <h2>12. Contact Us</h2>
                <p>
                        If you have any questions about this Privacy Policy, wish to exercise your data rights, or want to submit a driver data removal request, please contact us at:
                </p>
                <ul>
                        <li>
                                Email: <a href="mailto:contact@iracingdata.com">contact@iracingdata.com</a>
                        </li>
                        <li>
                                Website: <a href="https://iracingdata.com">iracingdata.com</a>
                        </li>
                </ul>

                <p>Last updated: March 21, 2026</p>
        </div>
);

export default PrivacyPolicy;
